import React, { useState, useEffect, useRef, useCallback } from "react";
import SmartSearchNavbar from "./components/SmartSearchNavbar";
import Sidebar from "./components/Sidebar";
import PersonModal from "./components/PersonModal";
import { FetchFromServer } from "../src/api/ServerApi";
import { usePartySymbolImage } from "../src/hooks/usePartySymbolImage";
import { useUser } from "./context/UserContext";


function MainPage() {
  const { userDetails, candidateMappings, fetchUserData } = useUser();

  const [results, setResults] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);

  const [panels, setPanels] = useState([]);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [selectedCorporationId, setSelectedCorporationId] = useState(null);

  const [bgColor, setBgColor] = useState("#ffffff");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // pagination
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const containerRef = useRef(null);

  // layout constants
  const ROW_HEIGHT = 18;
  const HEADER_HEIGHT = 36;
  const STATS_HEIGHT = 36;
  const TOP_TOOLBAR_HEIGHT = 36;
  const BOTTOM_SUMMARY_HEIGHT = 34;

  // 🔹 Party symbol image (via reusable hook)
  const { imageUrl: partySymbolImg } = usePartySymbolImage(
    selectedPanel?.PartySymbolImageId
  );

  // ---------------- Page size calc ----------------
  useEffect(() => {
    const computePageSize = () => {
      const vh = window.innerHeight;
      const reserved =
        HEADER_HEIGHT +
        STATS_HEIGHT +
        TOP_TOOLBAR_HEIGHT +
        BOTTOM_SUMMARY_HEIGHT +
        28;

      const available = Math.max(0, vh - reserved);
      const rows = Math.max(3, Math.floor(available / ROW_HEIGHT));

      setPageSize(rows);
      setPageIndex((p) =>
        Math.min(p, Math.floor(Math.max(0, results.length - 1) / rows))
      );
    };

    computePageSize();
    window.addEventListener("resize", computePageSize);
    return () => window.removeEventListener("resize", computePageSize);
  }, [results.length]);

  

  // ---------------- Search handler ----------------
  const handleSearchResults = (data) => {
    setResults(data || []);
    setPageIndex(0);
  };

  // ---------------- Panel background color ----------------
 const fetchColorByPanel = useCallback(() => {
  if (!userDetails) return;

  // 1️⃣ If a panel is selected → find matching panel
  if (selectedPanel && candidateMappings.length > 0) {
    const match = candidateMappings.find(
      (x) =>
        Number(x.PanelNumber) === Number(selectedPanel.PanelNumber) &&
        Number(x.CorporationId) === Number(selectedPanel.CorporationId)
    );

    if (match) {
      setBgColor(
        `rgb(${match.Back_RedCPer}, ${match.Back_GreenPer}, ${match.Back_BluePer})`
      );
      return;
    }
  }

  // 2️⃣ Fallback → use user default background (if exists)
  if (
    userDetails.Back_RedCPer !== undefined &&
    userDetails.Back_GreenPer !== undefined &&
    userDetails.Back_BluePer !== undefined
  ) {
    setBgColor(
      `rgb(${userDetails.Back_RedCPer}, ${userDetails.Back_GreenPer}, ${userDetails.Back_BluePer})`
    );
  }
}, [selectedPanel, candidateMappings, userDetails]);


  useEffect(() => {
    fetchColorByPanel();
  }, [fetchColorByPanel]);

  // ---------------- Refresh panels ----------------
  const refreshPanels = async () => {
    const mobile = localStorage.getItem("MobileNumber");
    if (!mobile) return;

    try {
      const data = await FetchFromServer(
        "GetListOfCorpPanelsForUserbyMobileNumber",
        { MobileNumber: mobile }
      );

      const mappings = data[0]?.CandidateMappings || [];

      const formattedPanels = mappings.map((item) => ({
        PanelNumber: item.PanelNumber,
        CorporationId: item.CorporationId,
        CorporationName: item.CorporationName,
        CorpCandidateMappingId: item.CorpCandidateMappingId,
        PartySymbolImageId: item.PartySymbolImageId,
      }));

      setPanels(formattedPanels);
      setSelectedPanel(formattedPanels[0] || null);
      setSelectedCorporationId(formattedPanels[0]?.CorporationId ?? null);
    } catch { }
  };

  // ---------------- Row click ----------------
  const handleRowClick = (rowIndex) => {
    const idx = pageIndex * pageSize + rowIndex;
    if (results[idx]) {
      setSelectedPerson(results[idx]);
      setShowModal(true);
    }
  };

  // pagination helpers
  const totalPages = Math.max(
    1,
    Math.ceil(results.length / Math.max(1, pageSize))
  );
  const pageStart = pageIndex * pageSize;
  const pageSlice = results.slice(pageStart, pageStart + pageSize);

  const mixSummary = { totalFound: results.length };

  const colPercentWidths = {
    PanelNumber: "3%",
    VoterNumber: "8%",
    Assembly: "10%",
    esurname: "8%",
    name_english: "12%",
    relative_english: "10%",
    age: "3%",
    sex: "3%",
    CardNo: "8%",
    MobileNumber: "6%",
    address: "30%",
  };

  const COLUMN_LABELS = {
    PanelNumber: "Panel No",
    esurname: "Surname",
    name_english: "Name",
    relative_english: "Relative Name",
    age: "Age",
    sex: "Gend",
    VoterNumber: "Voter No",
    CardNo: "Card No",
    MobileNumber: "Mobile",
    address: "Address",
    Assembly: "Assembly",
  };


  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <div
        style={{
          width: sidebarOpen ? 280 : 0,
          transition: "width 0.25s ease",
          overflow: "hidden",
          background: "#fff",
          borderRight: sidebarOpen ? "1px solid rgba(0,0,0,0.1)" : "none",
        }}
      >
        {sidebarOpen && (
          <Sidebar
            selectedPanel={selectedPanel}
            refreshPanels={refreshPanels}
          />
        )}
      </div>


      <div
        ref={containerRef}
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          background: bgColor,
          color: "black",
        }}
      >
        <SmartSearchNavbar
          onSearchResults={handleSearchResults}
          panels={panels}
          setPanels={setPanels}
          selectedPanel={selectedPanel}
          setSelectedPanel={setSelectedPanel}
          selectedCorporationId={selectedCorporationId}
          setSelectedCorporationId={setSelectedCorporationId}
          partySymbolImg={partySymbolImg}
        />

        {/* Toolbar */}
        <div style={{ display: "flex", padding: "4px 6px", gap: 8 }}>
          <button
            onClick={() => setSidebarOpen((s) => !s)}
            style={{
              border: "1px solid rgba(0,0,0,0.1)",
              background: "#fff",
              padding: "4px 8px",
              borderRadius: 6,
              cursor: "pointer",
              fontSize: 12,
            }}
          >
            {sidebarOpen ? "Hide Sidebar" : "Show Sidebar"}
          </button>


          <div style={{ fontSize: 12, fontWeight: 600 }}>Search Results</div>
          <div style={{ flex: 1 }} />

          {totalPages > 1 && (
            <div style={{ display: "flex", gap: 6, fontSize: 12 }}>
              <button
                onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                disabled={pageIndex === 0}
              >
                Prev
              </button>
              <div>
                {pageIndex + 1}/{totalPages}
              </div>
              <button
                onClick={() =>
                  setPageIndex((p) => Math.min(totalPages - 1, p + 1))
                }
                disabled={pageIndex >= totalPages - 1}
              >
                Next
              </button>
            </div>
          )}
        </div>

        {/* Content */}
        <div style={{ flex: 1, padding: 6, overflow: "hidden" }}>
          {results.length === 0 ? (
            <div
              style={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 16,
              }}
            >
              {partySymbolImg && (
                <img
                  src={partySymbolImg}
                  alt="Party Symbol"
                  style={{
                    width: 260,
                    height: 260,
                    objectFit: "contain",
                    background: "rgba(255,255,255,0.85)",
                    padding: 16,
                    borderRadius: 16,
                    boxShadow: "0 8px 22px rgba(0,0,0,0.25)",
                  }}
                />
              )}
              <div style={{ fontSize: 13 }}>No results</div>
            </div>
          ) : (
            (() => {
              const first = results[0] || {};
              const columns = Object.keys(first).filter((k) => {
                const v = first[k];

                // hide unwanted columns
                if (
                  k === "raw" ||
                  k === "CandidateFullName" ||
                  k === "CandidateMobile1" ||
                  k === "cardno" ||
                  k === "constno"
                ) {
                  return false;
                }

                // ✅ IMPORTANT FIX:
                // show PanelNumber column ONLY when selected panel number is 0
                if (k === "PanelNumber" && Number(selectedPanel?.PanelNumber) !== 0) {
                  return false;
                }


                return true;
              });


              const widths = columns.map(
                (c) => colPercentWidths[c] || "auto"
              );

              return (
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: 12,
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    {columns.map((c, i) => (
                      <col key={c} style={{ width: widths[i] }} />
                    ))}
                  </colgroup>

                  <thead>
                    <tr>
                      {columns.map((c) => (
                        <th
                          key={c}
                          style={{
                            textAlign: "left",
                            borderBottom: "1px solid rgba(0,0,0,0.12)",
                            padding: "4px 6px",
                          }}
                        >
                          {COLUMN_LABELS[c] || c}
                        </th>
                      ))}

                    </tr>
                  </thead>

                  <tbody>
                    {pageSlice.map((row, i) => (
                      <tr
                        key={i}
                        onClick={() => handleRowClick(i)}
                        style={{ cursor: "pointer" }}
                      >
                        {columns.map((c) => (
                          <td
                            key={c}
                            style={{
                              padding: "2px 6px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {row[c] ?? ""}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()
          )}
        </div>

        {/* Footer */}
        <div
          style={{
            background: "#fff",
            borderTop: "1px solid rgba(0,0,0,0.1)",
            padding: "8px 10px",
            display: "flex",
            justifyContent: "space-between",
            fontSize: 13,
          }}
        >
          <div>Smart Search - 9767728530</div>
          <div>
            Found: {mixSummary.totalFound} | Page {pageIndex + 1}/{totalPages}
          </div>
        </div>

        {showModal && (
          <PersonModal
            person={selectedPerson}
            onClose={() => setShowModal(false)}
          />
        )}
      </div>
    </div>
  );
}

export default MainPage;
